import { createReducer, on } from '@ngrx/store';
import { Option } from '@sapphire/result';
import { SupplierApiActions } from '@ps/survey-data-access';
import {
  classifySurvey,
  resolveTrackerChild,
  Survey,
  SURVEY_CLASSIFICATION_KIND,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import { coreContextOf } from '../../utils/core-context/survey-creation-action-core-context.helper';

import {
  DEFAULT_SURVEY_CHILD_KEY,
  INITIAL_SURVEY_CREATION_CORE_STATE,
} from '../survey-creation-state.constants';
import {
  SurveyChildRegistryState,
  SurveyCreationCoreState,
} from '../survey-creation-state.model';
import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from './survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationAudienceApiActions,
  SurveyCreationChildrenApiActions,
  SurveyCreationMcApiActions,
  SurveyCreationPreDraftActions,
  SurveyCreationTrackerApiActions,
} from '../audience/survey-creation-audience.actions';
import { SurveyCreationInsightsApiActions } from '../insights/survey-creation-insights.actions';
import {
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from '../launch/survey-creation-launch.actions';
import {
  buildLoadedRegistryChild,
  keyedLoadedRegistryChildren,
} from '../audience/domain/build-audience-form-state-from-survey';
import {
  buildMcPreDraftCoreChildren,
  buildTrackerPreDraftCoreChildren,
  describesActiveCoreChild,
  initializeCoreState,
  isLoadedCoreSibling,
  patchCoreChildren,
  refreshRegisteredWaves,
  resolveLoadedActiveChildKey,
  resolveReTargetedActiveChildKey,
  resolveLoadedParticipant,
  withActiveScheduleOnEveryWave,
  priceFieldsOf,
} from './utils/core-children.helper';
import { resolvePreDraftKind } from './utils/core-kind.helper';
import { omitChild } from '../audience/utils/audience-children.helper';
import {
  CHILD_SCOPE,
  collapseToDefaultChild,
  delegateRecordToDefaultChild,
} from '../survey-creation-child-registry.helper';

function initializeForSurvey(
  state: SurveyCreationCoreState,
  surveyId: Nullable<number>,
): SurveyCreationCoreState {
  const alreadyHydrated = !!surveyId && state.rootSurveyId === surveyId;

  if (alreadyHydrated) {
    return state;
  }

  // A locale switch re-`init`s with a sibling's id. The family is already
  // in the registry, so keep it and only re-target — resetting here would
  // force a refetch of every sibling.
  if (!!surveyId && isLoadedCoreSibling(state.children, surveyId)) {
    return {
      ...state,
      rootSurveyId: surveyId,
      activeChildKey: resolveReTargetedActiveChildKey(state, surveyId),
    };
  }

  return initializeCoreState(surveyId);
}

export const surveyCreationCoreReducer = createReducer(
  INITIAL_SURVEY_CREATION_CORE_STATE,

  // Only the audience page opens the create flow, so only its init keeps `trackerConfig`/`kind`.
  on(
    SurveyCreationAudienceActions.init,
    (state, { surveyId, activeChildKey }): SurveyCreationCoreState => ({
      ...initializeForSurvey(state, surveyId),
      activeChildKey: activeChildKey ?? null,
      trackerConfig: state.trackerConfig,
      kind: state.trackerConfig
        ? resolvePreDraftKind(state.trackerConfig)
        : state.kind,
    }),
  ),

  on(
    SurveyCreationLaunchActions.init,
    (state, { surveyId, activeChildKey }): SurveyCreationCoreState => ({
      ...initializeForSurvey(state, surveyId),
      activeChildKey: activeChildKey ?? null,
    }),
  ),

  on(
    SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess,
    (state, { surveys }): SurveyCreationCoreState =>
      patchCoreChildren(
        {
          ...state,
          children: {
            ...state.children,
            ...keyedLoadedRegistryChildren(
              surveys.filter(({ id }) => !state.children[id]),
            ),
          },
        },
        CHILD_SCOPE.keys(surveys.map(({ id }) => id)),
        { error: null },
      ),
  ),

  // The parent only holds the channel's targeting, so it takes no part in saves or recalculation.
  on(
    SurveyCreationCoreChildrenApiActions.loadSpecificAudienceParentSuccess,
    (state, { parentId }): SurveyCreationCoreState => ({
      ...state,
      children: {
        ...state.children,
        [parentId]: { survey: null, error: null, participant: false },
      },
    }),
  ),

  on(
    SurveyCreationCoreChildrenApiActions.loadChildSurveysFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  on(
    SurveyCreationMcApiActions.addSurveyChildSuccess,
    SurveyCreationTrackerApiActions.addWaveSuccess,
    (state, { survey }): SurveyCreationCoreState =>
      patchCoreChildren(
        {
          ...state,
          children: {
            ...state.children,
            [survey.id]: buildLoadedRegistryChild(survey),
          },
        },
        CHILD_SCOPE.keys([survey.id]),
        { error: null },
      ),
  ),

  on(
    SurveyCreationMcApiActions.deleteSurveyChildSuccess,
    (state, { surveyId }): SurveyCreationCoreState =>
      patchCoreChildren(
        { ...state, children: omitChild(state.children, surveyId) },
        CHILD_SCOPE.ALL,
        { error: null },
      ),
  ),

  // Both mean the same thing to this registry: the child's survey object was
  // refreshed (a CPI update, or a locale that just moved to LIVE) — patch it
  // in place, without resetting or navigating away.
  on(
    SurveyCreationTrackerApiActions.deleteWaveSuccess,
    (state, { surveyId, surveys }): SurveyCreationCoreState =>
      patchCoreChildren(
        {
          ...state,
          children: refreshRegisteredWaves(
            omitChild(state.children, surveyId),
            surveys,
          ),
        },
        CHILD_SCOPE.ALL_PARTICIPANTS,
        { error: null },
      ),
  ),

  on(
    SurveyCreationTrackerApiActions.updateLaunchDateSuccess,
    (state, { surveys }): SurveyCreationCoreState =>
      patchCoreChildren(
        {
          ...state,
          children: refreshRegisteredWaves(state.children, surveys),
        },
        CHILD_SCOPE.ALL_PARTICIPANTS,
        { error: null },
      ),
  ),

  on(
    SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess,
    (state, { survey }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.keys([survey.id]), (child) => ({
        survey: Option.from(child.survey).mapOr(survey, (held) => ({
          ...held,
          ...priceFieldsOf(survey),
        })),
        error: null,
      })),
  ),

  // Cross-listens to an insights action it does not own: a managed Apply persists
  // the survey-wide price override, so the saved survey's offerPrice follows. The
  // Target Margin row is seeded from this survey, so without it the row's Buyer
  // CPI keeps the old figure and wins over the applied one on save.
  on(
    SurveyCreationInsightsApiActions.syncGlobalPriceOverride,
    (state, { offerPrice }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, (child) =>
        Option.from(child.survey).mapOr({}, (survey) => ({
          survey: { ...survey, offerPrice: offerPrice ?? undefined },
        })),
      ),
  ),

  // The managed Apply persists the manual SCPI override on the survey and the PUT
  // never echoes it back, so the saved survey follows. The Target Margin row reads
  // it to lock the margin while the operator prices each supplier by hand; a
  // non-managed Apply omits the key and leaves the survey alone.
  on(
    SupplierApiActions.updateAllocationsSuccess,
    (state, action): SurveyCreationCoreState =>
      'manualScpiOverrideEnabled' in action
        ? patchCoreChildren(state, CHILD_SCOPE.ACTIVE, (child) =>
            Option.from(child.survey).mapOr({}, (survey) => ({
              survey: {
                ...survey,
                isManualScpiOverrideEnabled: !!action.manualScpiOverrideEnabled,
              },
            })),
          )
        : state,
  ),

  on(
    SurveyCreationChildrenApiActions.updateChildSurveyCpiFailure,
    (state, { surveyId, error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.keys([surveyId]), { error }),
  ),

  on(
    SurveyCreationMcApiActions.addSurveyChildFailure,
    SurveyCreationMcApiActions.deleteSurveyChildFailure,
    SurveyCreationTrackerApiActions.addWaveFailure,
    SurveyCreationTrackerApiActions.deleteWaveFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  on(
    SurveyCreationCoreActions.setActiveChildKey,
    (state, { activeChildKey }): SurveyCreationCoreState => ({
      ...state,
      activeChildKey,
    }),
  ),

  on(
    SurveyCreationCoreActions.resetActiveSurveyId,
    (state): SurveyCreationCoreState =>
      patchCoreChildren(
        {
          ...state,
          rootSurveyId: null,
          activeChildKey: null,
          kind: null,
          children: delegateRecordToDefaultChild(
            state.children,
            state.activeChildKey ?? DEFAULT_SURVEY_CHILD_KEY,
          ),
        },
        CHILD_SCOPE.keys([DEFAULT_SURVEY_CHILD_KEY]),
        { survey: null, error: null, participant: null },
      ),
  ),

  // Pre-draft only: before a surveyId exists, each locale gets its own
  // registry entry, mirroring audience's own per-locale children.
  on(
    SurveyCreationPreDraftActions.buildPreDraftChildren,
    (state, { localeData }): SurveyCreationCoreState => {
      if (state.rootSurveyId) {
        return state;
      }

      const { numberOfWaves } = state.trackerConfig ?? {};

      return {
        ...state,
        children: numberOfWaves
          ? buildTrackerPreDraftCoreChildren(state.children, numberOfWaves)
          : buildMcPreDraftCoreChildren(state.children, localeData),
      };
    },
  ),

  on(
    SurveyCreationPreDraftActions.selectAllPreDraftChildren,
    (state): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ALL, { participant: true }),
  ),

  on(
    SurveyCreationPreDraftActions.clearAllPreDraftChildren,
    (state): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ALL, { participant: false }),
  ),

  // `ids` is the complete checked set from the pre-draft table, not an
  // incremental target list — every child needs its `participant` flag
  // evaluated against it, so this stays CHILD_SCOPE.ALL rather than
  // CHILD_SCOPE.keys(ids) (which would leave unchecked children untouched
  // instead of flipping them back to false).
  on(
    SurveyCreationPreDraftActions.selectPreDraftChildren,
    (state, { ids }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ALL, (_, key) => ({
        participant: ids.includes(key),
      })),
  ),

  on(
    SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess,
    (state, { survey }): SurveyCreationCoreState => ({
      ...patchCoreChildren(state, CHILD_SCOPE.keys([survey.id]), {
        survey,
        error: null,
        participant: resolveLoadedParticipant(survey),
      }),
      activeChildKey: resolveLoadedActiveChildKey(state.activeChildKey, survey),
      kind: classifySurvey(survey),
    }),
  ),

  on(
    SurveyCreationCoreApiActions.loadRootSurveyForEditFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  on(
    SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
    (state, { survey }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, {
        survey,
        error: null,
        participant: resolveLoadedParticipant(survey),
      }),
  ),

  on(
    SurveyCreationCoreApiActions.loadActiveSurveyForEditFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  on(
    SurveyCreationAudienceApiActions.createStandardSurveySuccess,
    SurveyCreationAudienceApiActions.createSurveyFromTemplateSuccess,
    (state, { survey }): SurveyCreationCoreState => ({
      ...state,
      rootSurveyId: survey.id,
      activeChildKey: survey.id,
      kind: classifySurvey(survey),
      children: {
        [survey.id]: {
          survey,
          error: null,
          participant: true,
        },
      },
    }),
  ),

  on(
    SurveyCreationAudienceApiActions.createStandardSurveyFailure,
    SurveyCreationAudienceApiActions.createSurveyFromTemplateFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  on(
    SurveyCreationCoreApiActions.saveSurveySuccess,
    // Only the survey itself is withheld when the response describes some other
    // child — the error is cleared either way, because a save did succeed.
    (state, { survey }): SurveyCreationCoreState =>
      describesActiveCoreChild(state, survey)
        ? {
            ...patchCoreChildren(state, CHILD_SCOPE.ACTIVE, {
              survey,
              error: null,
            }),
            kind: classifySurvey(survey),
          }
        : patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error: null }),
  ),

  // The edits survive a failed save so the surface that dispatched it can stay
  // open on them; the message is already toasted by the save effect.
  on(
    SurveyCreationCoreApiActions.saveSurveyFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  on(
    SurveyCreationCoreApiActions.saveSiblingSurveySuccess,
    (state, { survey }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.keys([survey.id]), {
        survey,
        error: null,
      }),
  ),

  on(
    SurveyCreationCoreApiActions.saveSiblingSurveyFailure,
    (state, { key, error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.keys([key]), { error }),
  ),

  // Patch returns void — no fresh survey comes back — so this merges the
  // TEST button's own patched fields into the registry's own survey directly,
  // instead of tracking a second copy of them elsewhere.
  on(
    SurveyCreationLaunchApiActions.patchTestUrlsSuccess,
    (state, { liveUrl, testUrl, testEntryLink }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, (child) =>
        child.survey
          ? {
              survey: {
                ...child.survey,
                liveUrl,
                testUrl,
                testPsSurveyEntryLink: testEntryLink,
              },
              error: null,
            }
          : {},
      ),
  ),

  // Ticking a Tracker's "same Launch Settings" shows the active wave's launch
  // date on every wave in the drawer at once.
  on(
    SurveyCreationLaunchActions.updateSameSettings,
    (state, action): SurveyCreationCoreState =>
      action.sameSettings.launchFeatures &&
      Option.from(coreContextOf(action).kind).mapOr(
        false,
        ({ kind }) => kind === SURVEY_CLASSIFICATION_KIND.TRACKER,
      )
        ? withActiveScheduleOnEveryWave(state)
        : state,
  ),

  // The wave exclusions are patched into the registry's own survey directly to be available to the tracker details.
  on(
    SurveyCreationLaunchActions.updateTrackerWaveExclusions,
    (state, { waveExclusions }): SurveyCreationCoreState =>
      patchCoreChildren(
        state,
        CHILD_SCOPE.ACTIVE,
        (child): Partial<SurveyChildRegistryState> =>
          Option.from(child.survey)
            .andThen((survey) =>
              Option.from(resolveTrackerChild(survey)).map((trackerChild) => ({
                survey: {
                  ...survey,
                  project: { ...trackerChild, waveExclusions },
                },
                error: null,
              })),
            )
            .unwrapOr({}),
      ),
  ),

  // Merges the basic token into the registry's own survey; the launch page
  // re-maps its form from the survey, so a reverted value reaches the toggle.
  on(
    SurveyCreationLaunchApiActions.basicTokenPatched,
    (state, { basicToken }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, (child) =>
        child.survey
          ? { survey: { ...child.survey, basicToken }, error: null }
          : {},
      ),
  ),

  // Patches the launched child's own status in place instead of waiting on a
  // reload — an MC Traditional survey with sibling locales still Draft stays
  // mounted after this (see changeLaunchStatus$), so the drawer needs to see
  // the new status immediately.
  on(
    SurveyCreationLaunchApiActions.launchSurveySuccess,
    (state, { surveyId, status }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.keys([surveyId]), (child) =>
        child.survey ? { survey: { ...child.survey, status } } : {},
      ),
  ),

  on(
    SurveyCreationCoreApiActions.localeSurveyRefreshed,
    (state, { survey }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.keys([survey.id]), {
        survey,
        error: null,
      }),
  ),

  // Children are not re-fetched after the PATCH (Traditional MC) or the
  // project PUT (Group MC, Tracker); the registry keeps what was sent so the
  // next child, the next save and the File Links check see it.
  on(
    SurveyCreationLaunchApiActions.localeSettingsSynced,
    (state, { updates }): SurveyCreationCoreState =>
      withSyncedChildren(state, updates),
  ),

  on(
    SurveyCreationLaunchApiActions.projectLaunchSettingsSynced,
    (state, { updates }): SurveyCreationCoreState =>
      withSyncedChildren(state, updates),
  ),

  on(
    SurveyCreationCoreActions.patchActiveSurvey,
    (state, { patch, scope }): SurveyCreationCoreState =>
      patchCoreChildren(state, scope, (child) =>
        child.survey
          ? { survey: { ...child.survey, ...patch }, error: null }
          : {},
      ),
  ),

  on(
    SurveyCreationCoreApiActions.cloneSurveyFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  // Clearing a stale registry error, not writing per-child data.
  on(
    SurveyCreationAudienceApiActions.loadSurveyTagsSuccess,
    SurveyCreationAudienceApiActions.linkSurveyTagsSuccess,
    (state): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ALL, { error: null }),
  ),

  on(
    SurveyCreationAudienceActions.applyAdvancedFilters,
    (state): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ALL, { error: null }),
  ),

  on(
    SurveyCreationCoreActions.setupTrackerConfig,
    (state, { config }): SurveyCreationCoreState => ({
      ...state,
      trackerConfig: config,
      kind: resolvePreDraftKind(config),
    }),
  ),

  on(
    SurveyCreationCoreActions.resetTrackerConfig,
    (state): SurveyCreationCoreState => ({
      ...state,
      trackerConfig: null,
      kind: null,
      activeChildKey: null,
      children: collapseToDefaultChild(state.children),
    }),
  ),

  on(
    SurveyCreationCoreActions.reset,
    (): SurveyCreationCoreState => INITIAL_SURVEY_CREATION_CORE_STATE,
  ),
);

function withSyncedChildren(
  state: SurveyCreationCoreState,
  updates: readonly {
    readonly surveyIds: readonly number[];
    readonly changes: Partial<Survey>;
  }[],
): SurveyCreationCoreState {
  return updates.reduce(
    (patched, { surveyIds, changes }) =>
      patchCoreChildren(patched, CHILD_SCOPE.keys(surveyIds), (child) =>
        child.survey
          ? { survey: { ...child.survey, ...changes }, error: null }
          : {},
      ),
    state,
  );
}
